import { Extension } from "@tiptap/react";

/**
 * Inline CSS `text-transform` (e.g. uppercase) on a text selection.
 *
 * Implemented as a global attribute on the `textStyle` mark — the same mark
 * fontSize/color/fontFamily ride on — so it composes with them on one span and
 * round-trips through HTML like the others. Toggling sets/clears the mark over
 * the selection, mirroring bold/italic (a per-selection inline style), not a
 * block-level attribute.
 *
 * Kept as its own extension (rather than baked into TextStyleExtended) so it's
 * isolated and unit-testable without the full editor module.
 */
declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        textTransform: {
            /** Set a text-transform value (e.g. "uppercase") on the selection. */
            setTextTransform: (value: string) => ReturnType;
            /** Remove the text-transform from the selection. */
            unsetTextTransform: () => ReturnType;
            /** Toggle a text-transform value on/off for the selection. */
            toggleTextTransform: (value: string) => ReturnType;
        };
    }
}

interface TextTransformOptions {
    /** Which extensions' nodes/marks carry the attribute. */
    types: string[];
}

export const TextTransform = Extension.create<TextTransformOptions>({
    name: "textTransform",

    addOptions() {
        return {
            types: ["textStyle"],
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    textTransform: {
                        default: null,
                        parseHTML: (element) =>
                            element.style.textTransform || null,
                        renderHTML: (attributes) => {
                            if (!attributes.textTransform) {
                                return {};
                            }
                            return {
                                style: `text-transform: ${attributes.textTransform}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setTextTransform:
                (value: string) =>
                ({ commands }) =>
                    commands.setMark("textStyle", { textTransform: value }),

            unsetTextTransform:
                () =>
                ({ chain }) =>
                    chain()
                        .setMark("textStyle", { textTransform: null })
                        .removeEmptyTextStyle()
                        .run(),

            toggleTextTransform:
                (value: string) =>
                ({ editor, chain }) => {
                    const isActive =
                        editor.getAttributes("textStyle")?.textTransform ===
                        value;
                    if (isActive) {
                        return chain()
                            .setMark("textStyle", { textTransform: null })
                            .removeEmptyTextStyle()
                            .run();
                    }
                    return chain()
                        .setMark("textStyle", { textTransform: value })
                        .run();
                },
        };
    },
});
